// ── COMMUNITY ─────────────────────────────────────────────
import React, { useEffect, useState } from 'react'
import { useAuth } from '../App'
import { searchUsers, sendFriendRequest, getFriends, sendNudge } from '../lib/supabase'

function Initials({ name }) {
  const parts = (name || '?').split(' ')
  const ini = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)
  return ini.toUpperCase()
}

export function Community() {
  const { user } = useAuth()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [friends, setFriends] = useState([])
  const [sent, setSent] = useState(new Set())

  useEffect(() => { if (user) getFriends(user.id).then(setFriends) }, [user])

  useEffect(() => {
    if (!query.trim()) { setResults([]); return }
    const t = setTimeout(() => searchUsers(query).then(setResults), 400)
    return () => clearTimeout(t)
  }, [query])

  const friendIds = new Set(friends.map(f => f.id))

  const handleAdd = async (targetId) => {
    await sendFriendRequest(user.id, targetId)
    setSent(s => new Set([...s, targetId]))
  }

  const handleNudge = async (targetId, name) => {
    await sendNudge(user.id, targetId, `Αντε ${name}, σήμερα παίζουμε! 🎵`)
    alert('Nudge στάλθηκε!')
  }

  const today = new Date().toDateString()

  return (
    <div style={{ padding: '20px 16px' }}>
      <div className="fade-up" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>We play music</div>
        <div className="page-title">Η <span style={{ color: 'var(--blue)' }}>ομάδα</span> σου</div>
      </div>

      <input className="input fade-up fade-up-1" placeholder="🔍 Αναζήτηση χρηστών..." value={query}
        onChange={e => setQuery(e.target.value)} style={{ marginBottom: 20 }} />

      {results.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div className="section-label">Αποτελέσματα</div>
          {results.filter(r => r.id !== user?.id).map(r => (
            <div key={r.id} className="card" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="avatar avatar-md"><Initials name={r.full_name || r.username} /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{r.full_name || r.username}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.instrument} · {r.level}</div>
                {r.current_streak > 1 && <div className="streak-badge" style={{ marginTop: 4 }}>🔥 {r.current_streak}</div>}
              </div>
              {friendIds.has(r.id) ? (
                <span style={{ fontSize: 12, color: 'var(--green)' }}>✓ Φίλος</span>
              ) : sent.has(r.id) ? (
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>Εστάλη</span>
              ) : (
                <button onClick={() => handleAdd(r.id)}
                  style={{ padding: '8px 14px', borderRadius: 20, border: '1px solid var(--blue)', background: 'var(--blue-dim)', color: 'var(--blue)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                  + Πρόσθεσε
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="fade-up fade-up-2">
        <div className="section-label">Οι φίλοι σου ({friends.length})</div>
        {friends.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🎵</div>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Αναζήτησε χρήστες και πρόσθεσέ τους!</div>
          </div>
        ) : friends.map(f => {
          const playedToday = f.last_session_date && new Date(f.last_session_date).toDateString() === today
          return (
            <div key={f.id} className="card" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="avatar avatar-md" style={{ background: playedToday ? 'var(--green-dim)' : 'var(--blue-dim)', color: playedToday ? 'var(--green)' : 'var(--blue)' }}>
                <Initials name={f.full_name || f.username} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{f.full_name || f.username}</div>
                <div style={{ fontSize: 12, color: playedToday ? 'var(--green)' : 'var(--text-muted)' }}>
                  {playedToday ? '✅ Έπαιξε σήμερα' : '⏳ Δεν έπαιξε σήμερα'}
                </div>
              </div>
              {!playedToday && (
                <button onClick={() => handleNudge(f.id, f.full_name || f.username)}
                  style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                  👋
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── NOTIFICATIONS ─────────────────────────────────────────
import { getNudges, markNudgeRead } from '../lib/supabase'
import { formatDistanceToNow } from 'date-fns'
import { el } from 'date-fns/locale'

export function Notifications() {
  const { user } = useAuth()
  const [nudges, setNudges] = useState([])

  useEffect(() => {
    if (!user) return
    getNudges(user.id).then(setNudges)
  }, [user])

  const handleRead = async (id) => {
    await markNudgeRead(id)
    setNudges(n => n.filter(x => x.id !== id))
  }

  return (
    <div style={{ padding: '20px 16px' }}>
      <div className="fade-up" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Μηνύματα</div>
        <div className="page-title">Η ομάδα <span style={{ color: 'var(--blue)' }}>σε σκέφτεται</span></div>
      </div>

      {nudges.length === 0 ? (
        <div className="card fade-up" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🎵</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Δεν υπάρχουν νέα μηνύματα</div>
        </div>
      ) : nudges.map((n, i) => (
        <div key={n.id} className="card fade-up" style={{ marginBottom: 10, animationDelay: `${i * 0.05}s` }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--purple-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>👋</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                {n.profiles?.full_name || n.profiles?.username}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 6 }}>"{n.message}"</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: el })}
              </div>
            </div>
            <button onClick={() => handleRead(n.id)}
              style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 16 }}>✕</button>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── PROFILE ───────────────────────────────────────────────
import { updateProfile, signOut } from '../lib/supabase'

export function Profile() {
  const { user, setUser } = useAuth()
  const [editing, setEditing] = useState(false)
  const [fullName, setFullName] = useState(user?.full_name || '')
  const [bio, setBio] = useState(user?.bio || '')
  const [instrument, setInstrument] = useState(user?.instrument || 'piano')
  const [level, setLevel] = useState(user?.level || 'beginner')
  const [privacy, setPrivacy] = useState({
    privacy_time: user?.privacy_time ?? true,
    privacy_sections: user?.privacy_sections ?? true,
    privacy_streak: user?.privacy_streak ?? true,
    privacy_notes: user?.privacy_notes ?? false,
    privacy_stats: user?.privacy_stats ?? false,
  })
  const [timerMode, setTimerMode] = useState(user?.timer_mode || 'simple')

  const handleSave = async () => {
    const updated = await updateProfile(user.id, { full_name: fullName, bio, instrument, level, timer_mode: timerMode, ...privacy })
    setUser(updated); setEditing(false)
  }

  const PrivacyRow = ({ label, sub, key_ }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</div>
      </div>
      <button className={`toggle ${privacy[key_] ? 'on' : ''}`}
        onClick={() => setPrivacy(p => ({ ...p, [key_]: !p[key_] }))} />
    </div>
  )

  return (
    <div style={{ padding: '20px 16px' }}>
      <div className="fade-up" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div className="avatar avatar-lg" style={{ fontSize: 24 }}>
          <Initials name={user?.full_name || user?.username} />
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 500 }}>{user?.full_name || user?.username}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>@{user?.username}</div>
          {user?.current_streak > 0 && <div className="streak-badge" style={{ marginTop: 6 }}>🔥 {user.current_streak} μέρες</div>}
        </div>
      </div>

      {editing ? (
        <div className="fade-up" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          <input className="input" placeholder="Ονοματεπώνυμο" value={fullName} onChange={e => setFullName(e.target.value)} />
          <input className="input" placeholder="Bio (προαιρετικό)" value={bio} onChange={e => setBio(e.target.value)} />
          <select className="input" value={instrument} onChange={e => setInstrument(e.target.value)}>
            <option value="piano">🎹 Πιάνο</option>
            <option value="guitar">🎸 Κιθάρα</option>
            <option value="bass">🎸 Μπάσο</option>
            <option value="saxophone">🎷 Σαξόφωνο</option>
            <option value="drums">🥁 Drums</option>
            <option value="voice">🎤 Φωνή</option>
            <option value="other">🎵 Άλλο</option>
          </select>
          <select className="input" value={level} onChange={e => setLevel(e.target.value)}>
            <option value="beginner">Αρχάριος</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Προχωρημένος</option>
          </select>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn-primary" onClick={handleSave}>Αποθήκευση</button>
            <button className="btn-ghost" onClick={() => setEditing(false)}>Ακύρωση</button>
          </div>
        </div>
      ) : (
        <button className="btn-ghost fade-up" style={{ marginBottom: 20, width: '100%' }} onClick={() => setEditing(true)}>
          ✏️ Επεξεργασία προφίλ
        </button>
      )}

      {/* Privacy */}
      <div className="fade-up fade-up-2" style={{ marginBottom: 20 }}>
        <div className="section-label">Privacy — Τι βλέπουν οι άλλοι</div>
        <div className="card">
          <PrivacyRow label="Χρόνος μελέτης" sub="Πόση ώρα έπαιξες" key_="privacy_time" />
          <PrivacyRow label="Ενότητες session" sub="Τεχνική, Comping κτλ." key_="privacy_sections" />
          <PrivacyRow label="Streak" sub="Το σερί ημερών σου" key_="privacy_streak" />
          <PrivacyRow label="Σημειώσεις" sub="Τι έγραψες κατά τη μελέτη" key_="privacy_notes" />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Stats & γραφήματα</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Εβδομαδιαία πρόοδος</div>
            </div>
            <button className={`toggle ${privacy.privacy_stats ? 'on' : ''}`}
              onClick={() => setPrivacy(p => ({ ...p, privacy_stats: !p.privacy_stats }))} />
          </div>
        </div>
      </div>

      <button onClick={() => signOut()}
        style={{ width: '100%', padding: '14px', borderRadius: 'var(--radius)', border: '1px solid rgba(255,92,92,0.3)', background: 'rgba(255,92,92,0.08)', color: 'var(--red)', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font)' }}>
        Αποσύνδεση
      </button>
    </div>
  )
}

export default Community
